/**
 * CallButton — the single place every "Call merchant / Call customer /
 * Call rider" action in the app should go through.
 *
 * Root cause this exists for: every call-to-action across the app used
 * a plain ``<a href={`tel:${phone}`}>``. That's the correct pattern on
 * a normal mobile browser, but inside a Capacitor WKWebView it's a
 * well-known dead end — the WebView doesn't hand off unrecognised URL
 * schemes to the OS on its own, so tapping the link silently does
 * nothing (user-reported: "Call Merchant" button not working on
 * My Orders > Bookings). ``lib/openExternal.js`` already exists
 * specifically to solve this class of bug for other schemes
 * (https/geo/mailto) via Capacitor's ``AppLauncher`` plugin with a
 * documented fallback chain — this component is just that same fix
 * applied to phone calls, and gives every screen ONE shared place to
 * get it from instead of six independent (and until now, all equally
 * broken) copies of the same ``<a href="tel:">``.
 *
 * Deliberately unopinionated about appearance — every existing call
 * site had a different visual (icon+"Call" pill, bare "Call" chip,
 * raw phone-number link, etc.). Pass ``className``/``children`` to
 * keep whatever look a screen already has; only the click behaviour
 * is centralised.
 */
import { Phone } from "lucide-react";
import { openExternal } from "../lib/openExternal";

export default function CallButton({ phone, children, className, testid, onClick, ...rest }) {
    if (!phone) return null;
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(e);
        openExternal(`tel:${phone}`);
    };
    return (
        <button
            type="button"
            onClick={handleClick}
            data-testid={testid}
            className={className}
            {...rest}
        >
            {children || (<><Phone size={13} className="inline -mt-0.5 mr-1" /> Call</>)}
        </button>
    );
}
